import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchTeamFileScanningStatus } from '@/api/v4/team-files.api';
import { QueryKey } from '@/config/query-client';
import type { File } from '@/features/action-panel/files/file.interface';
import { notify } from '@/ui/snackbar/notify';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { teamIdState } from '../../../team.state';
import { useAtomValue } from 'jotai';

export const useTeamFileScanningStatus = (file: File) => {
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();
  const teamId = useAtomValue(teamIdState);
  const [isError, setError] = useState(false);
  const { hasRequiredPermission } = useUserPermissions();

  const fetchFileScanningStatus = async () => {
    if (!teamId) return;
    return fetchTeamFileScanningStatus({
      teamId,
      fileId: file.id,
    });
  };

  const query = useQuery({
    queryKey: [QueryKey.TeamFileScanningStatus, teamId, file.id],
    queryFn: fetchFileScanningStatus,
    enabled:
      !!teamId &&
      hasRequiredPermission(PERMISSION.VIEW_FILES) &&
      file.scanningStatus === 'unscanned',
    staleTime: Infinity,
    refetchInterval: query => {
      const file = query.state.data;
      return !isError && file && file.scanningStatus === 'unscanned'
        ? 1000
        : false;
    },
  });

  const scanningStatus = query.data?.scanningStatus;

  useEffect(() => {
    if (!scanningStatus || scanningStatus === 'unscanned') return;

    void queryClient.invalidateQueries({
      queryKey: [QueryKey.TeamFiles, teamId],
    });

    if (scanningStatus === 'infected' || scanningStatus === 'error') {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.TeamActionsInfo, teamId],
      });
      notify({
        message: t(
          scanningStatus === 'infected'
            ? 'actionPanel.files.infected'
            : 'actionPanel.files.scanningError',
          { name: file.name },
        ),
      });
    }
  }, [scanningStatus, queryClient, teamId, file.name, t]);

  useEffect(() => {
    if (query.isError) {
      setError(true);
      notify({ message: t`unknownError` });
    }
  }, [query.isError, t]);

  return query;
};
